import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Trophy, AlertTriangle, FilePlus, FileDown, X, Lightbulb, ShieldCheck, RotateCcw,
} from "lucide-react";

const monthNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtTl = (n) => `₺ ${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DriverDiscipline() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isDataEntry = user?.role === "data_entry";
  const canEdit = isAdmin || isDataEntry;      // İhlal Ekle, Sil, Karne PDF
  const canSeePrim = isAdmin || isDataEntry;    // Prim TL, Toplam Bütçe, Taban Prim
  const [month, setMonth] = useState(monthNow());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], base_tl: 5000, max_score: 100, weekly_start_month: "2026-10" });
  const [types, setTypes] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null); // row for history modal
  const [suggestions, setSuggestions] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const [karneLoadingId, setKarneLoadingId] = useState(null);
  const [weeklyEdits, setWeeklyEdits] = useState({}); // pid -> {w1..w4}
  const [savingWeekly, setSavingWeekly] = useState(null);
  const [onlyNoEntry, setOnlyNoEntry] = useState(false);

  const [form, setForm] = useState({ personnel_id: "", type_code: "", date: todayYmd(), plate: "", description: "", tutanak_no: "" });
  const [addFile, setAddFile] = useState(null);

  const isWeeklyMonth = useMemo(() => (month && (data.weekly_start_month || "2026-10") && month >= (data.weekly_start_month || "2026-10")), [month, data.weekly_start_month]);

  const load = async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        api.get("/driver-discipline/dashboard", { params: { month } }),
        api.get("/driver-discipline/violation-types"),
      ]);
      setData(d.data);
      setTypes(t.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally { setLoading(false); }
  };
  const loadSuggestions = async () => {
    if (!canEdit) return;
    setSuggLoading(true);
    try {
      const r = await api.get("/driver-discipline/suggestions", { params: { month } });
      setSuggestions(r.data.suggestions || []);
    } catch { /* silent */ }
    finally { setSuggLoading(false); }
  };
  useEffect(() => { load(); loadSuggestions(); /* eslint-disable-next-line */ }, [month]);

  const openAdd = (pid) => {
    setForm({ personnel_id: pid || "", type_code: "", date: todayYmd(), plate: "", description: "", tutanak_no: "" });
    setAddFile(null);
    setAddOpen(true);
  };

  const submitViolation = async () => {
    if (!form.personnel_id) return toast.error("Şoför seçin");
    if (!form.type_code) return toast.error("İhlal türü seçin");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) return toast.error("Tarih zorunlu");
    try {
      const res = await api.post("/driver-discipline/violations", form);
      // Dosya varsa 2. istekte yükle
      if (addFile && res?.data?.id) {
        try {
          const fd = new FormData();
          fd.append("file", addFile);
          await api.post(`/driver-discipline/violations/${res.data.id}/document`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (e) {
          toast.error("Ceza belgesi yüklenemedi: " + (formatApiErrorDetail(e.response?.data?.detail) || "hata"));
        }
      }
      const isPrimZero = selectedType?.prim_zero;
      toast.success(isPrimZero ? "Ceza kaydedildi — bu ay primi sıfırlandı" : "İhlal kaydedildi, puan güncellendi");
      setAddOpen(false);
      setAddFile(null);
      await load(); loadSuggestions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  const selectedType = useMemo(() => types.find((t) => t.code === form.type_code) || null, [types, form.type_code]);

  const revokeViolation = async (vid) => {
    if (!canEdit) return;
    if (!window.confirm("Bu ihlal kaydını iptal edip puanı iade etmek istediğinizden emin misiniz?")) return;
    try {
      await api.delete(`/driver-discipline/violations/${vid}`);
      toast.success("Kayıt iptal edildi, puan iade edildi");
      await load();
      if (historyRow) {
        setHistoryRow({ ...historyRow, violations: (historyRow.violations || []).filter((v) => v.id !== vid) });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İptal edilemedi");
    }
  };

  const acceptSuggestion = async (s) => {
    try {
      await api.post("/driver-discipline/violations", {
        personnel_id: s.personnel_id,
        type_code: s.type_code,
        date: s.date,
        plate: "",
        description: `Vardiya notu: ${s.excerpt}`.slice(0, 400),
      });
      toast.success(`${s.personnel_name} için ihlal kaydedildi`);
      await load(); loadSuggestions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  const downloadKarne = async (row) => {
    setKarneLoadingId(row.personnel_id);
    try {
      const res = await api.get("/driver-discipline/karne.pdf", {
        params: { personnel_id: row.personnel_id, month },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Disiplin_Karnesi_${row.personnel_name.replace(/\s+/g, "_")}_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF hazırlanamadı");
    } finally { setKarneLoadingId(null); }
  };

  // Update 29.15 — haftalık gün girişleri
  const getWeekVal = (row, k) => {
    const e = weeklyEdits[row.personnel_id];
    if (e && Object.prototype.hasOwnProperty.call(e, k)) return e[k];
    return (row.weekly_days || {})[k];
  };
  const setWeekVal = (row, k, raw) => {
    const trimmed = String(raw ?? "").trim();
    const v = trimmed === "" ? null : Math.max(0, parseInt(trimmed, 10) || 0);
    setWeeklyEdits((prev) => ({ ...prev, [row.personnel_id]: { ...(prev[row.personnel_id] || {}), [k]: v } }));
  };
  const hasPendingWeekly = (row) => {
    const e = weeklyEdits[row.personnel_id];
    if (!e) return false;
    const cur = row.weekly_days || {};
    return ["w1","w2","w3","w4"].some(k => Object.prototype.hasOwnProperty.call(e, k) && (e[k] ?? null) !== (cur[k] ?? null));
  };
  const saveWeekly = async (row) => {
    if (!canEdit) return;
    const payload = {
      personnel_id: row.personnel_id, month,
      w1: getWeekVal(row, "w1"), w2: getWeekVal(row, "w2"),
      w3: getWeekVal(row, "w3"), w4: getWeekVal(row, "w4"),
    };
    setSavingWeekly(row.personnel_id);
    try {
      await api.put("/driver-discipline/weekly-days", payload);
      toast.success(`${row.personnel_name}: haftalık gün kaydedildi`);
      setWeeklyEdits((prev) => { const n = { ...prev }; delete n[row.personnel_id]; return n; });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally { setSavingWeekly(null); }
  };
  const fillMissing = async () => {
    if (!canEdit) return;
    const n = (data.rows || []).filter(r => !r.has_weekly_entry).length;
    if (!window.confirm(`${n} şoförde boş alanlar her haftanın maksimum günüyle doldurulacak. Dolu alanlar DEĞİŞMEZ. Devam?`)) return;
    try {
      const r = await api.post("/driver-discipline/weekly-days/fill-missing", { month });
      toast.success(`${r.data.updated_count} şoför için boş alanlar dolduruldu`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız");
    }
  };
  const noEntryCount = useMemo(() => (data.rows || []).filter(r => !r.has_weekly_entry).length, [data.rows]);
  const filteredRows = useMemo(() => (onlyNoEntry ? (data.rows || []).filter(r => !r.has_weekly_entry) : (data.rows || [])), [data.rows, onlyNoEntry]);

  const stats = useMemo(() => {
    const rows = data.rows || [];
    const totalDrivers = rows.length;
    const withPenalty = rows.filter((r) => r.penalty_total > 0).length;
    const zeroed = rows.filter((r) => r.remaining_score === 0).length;
    const totalBonus = rows.reduce((s, r) => s + (r.bonus_tl || 0), 0);
    return { totalDrivers, withPenalty, zeroed, totalBonus };
  }, [data.rows]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto w-full" data-testid="driver-discipline-page">
      <div className="flex items-start sm:items-baseline justify-between flex-wrap gap-4 flex-col sm:flex-row">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Şoför Kadrosu · Disiplin Modeli</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Şoför Disiplin &amp; Prim</h1>
          <p className="mt-1 text-sm text-slate-500">
            Her şoför ay başında <b>100 puan</b> ile başlar; ihlaller puandan düşer.
            {canSeePrim && <> Ay sonu primi <b>Kalan Puan / 100 × Taban Prim</b> formülüyle hesaplanır.</>}
          </p>
        </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 sm:flex-none">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Ay</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value || monthNow())} className="mt-1 h-11 font-mono w-full sm:max-w-[160px]" data-testid="dd-month" />
        </div>
        {canEdit && (
          <Button
            onClick={() => openAdd(null)}
            className="bg-[#1E3A8A] hover:bg-[#1e40af] h-11 mt-6 sm:mt-6 shrink-0"
            data-testid="dd-add-btn"
          >
            <FilePlus className="w-4 h-4 mr-2" /> İhlal Ekle
          </Button>
        )}
      </div>
      </div>

      {/* KPI strip */}
      <div className={`grid grid-cols-2 ${canSeePrim ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3`}>
        <KpiCard label="Aktif Şoför" value={stats.totalDrivers} color="text-slate-900" />
        <KpiCard label="İhlali Olan" value={stats.withPenalty} color="text-amber-700" />
        <KpiCard label="Puanı Sıfır" value={stats.zeroed} color="text-red-600" />
        {canSeePrim && (
          <KpiCard label="Toplam Prim Bütçesi" value={fmtTl(stats.totalBonus)} color="text-emerald-700" mono />
        )}
      </div>

      {/* Update 29.15 — Haftalık gün toolbar */}
      {isWeeklyMonth && (data.rows || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded" data-testid="dd-weekly-toolbar">
          <div className="text-xs">
            <b className="text-amber-700" data-testid="dd-no-entry-count">{noEntryCount}</b> şoförün gün girişi yok · toplam {(data.rows || []).length} şoför
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="dd-only-no-entry-toggle">
            <input type="checkbox" checked={onlyNoEntry} onChange={(e) => setOnlyNoEntry(e.target.checked)} />
            <span>Sadece gün girilmemişleri göster</span>
          </label>
          {canEdit && noEntryCount > 0 && (
            <Button size="sm" variant="outline" onClick={fillMissing} data-testid="dd-fill-missing-btn">
              Boş olanlara tam gün yaz
            </Button>
          )}
        </div>
      )}

      {/* Suggestions from shift notes */}
      {canEdit && suggestions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="dd-suggestions">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h3 className="font-heading font-bold text-slate-900">Vardiya Notlarından Otomatik Öneriler ({suggestions.length})</h3>
              <Button size="sm" variant="ghost" onClick={loadSuggestions} disabled={suggLoading} className="ml-auto text-xs">
                {suggLoading ? "Yükleniyor..." : "Yenile"}
              </Button>
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {suggestions.map((s, i) => (
                <div key={i} className="border border-amber-200 bg-white rounded p-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <b className="text-slate-900">{s.personnel_name}</b>
                      <span className="mx-2 text-slate-400">·</span>
                      <span className="font-mono text-xs text-slate-500">{s.date}</span>
                      <span className="ml-2 text-xs text-red-700 font-bold">−{s.points} pt</span>
                    </div>
                    <div className="text-xs text-slate-700 mt-0.5">{s.type_label}</div>
                    <div className="text-xs text-slate-500 mt-1 italic truncate">"{s.excerpt}"</div>
                  </div>
                  <Button size="sm" onClick={() => acceptSuggestion(s)} className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid={`dd-accept-sugg-${i}`}>
                    Tek Tıkla İşle
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Driver table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ tableLayout: "fixed" }} data-testid="dd-driver-table">
              <colgroup>
                <col style={{ width: canSeePrim ? "24%" : "28%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: canSeePrim ? "28%" : "34%" }} />
                {canSeePrim && <col style={{ width: "14%" }} />}
                <col style={{ width: "8%" }} />
                <col style={{ width: canSeePrim ? "16%" : "20%" }} />
              </colgroup>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Şoför</th>
                  <th className="px-4 py-3 text-center text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Ceza</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Kalan Puan</th>
                  {canSeePrim && (
                    <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Prim (TL)</th>
                  )}
                  <th className="px-4 py-3 text-center text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">İhlal</th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={canSeePrim ? 6 : 5} className="text-center py-10 text-slate-400">Yükleniyor...</td></tr>
                )}
                {!loading && (data.rows || []).length === 0 && (
                  <tr><td colSpan={canSeePrim ? 6 : 5} className="text-center py-10 text-slate-400">Bu ayda kayıtlı aktif şoför bulunmuyor</td></tr>
                )}
                {!loading && filteredRows.map((r) => {
                  const pct = (r.remaining_score / (data.max_score || 100)) * 100;
                  const barColor = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-red-500";
                  return (
                    <tr key={r.personnel_id} data-testid={`dd-row-${r.personnel_id}`} className="border-b border-slate-100 hover:bg-slate-50/60 align-middle">
                      <td className="px-4 py-3">
                        <button
                          className="font-heading font-semibold text-slate-900 hover:text-blue-800 hover:underline truncate text-left"
                          onClick={() => isAdmin && navigate(`/soforler/${r.personnel_id}`)}
                          data-testid={`dd-name-${r.personnel_id}`}
                        >
                          {r.personnel_name}
                        </button>
                        <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                          Başlangıç 100
                          {r.prorated && (
                            <span className="ml-2 text-blue-700" data-testid={`dd-worked-${r.personnel_id}`}>
                              · {r.worked_days} / {r.days_in_month} gün
                            </span>
                          )}
                        </div>
                        {canSeePrim && r.prim_status === "PRIM_ZERO" && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700"
                            data-testid={`dd-prim-zero-badge-${r.personnel_id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Prim sıfırlandı — {r.prim_zero_label}
                          </div>
                        )}
                        {canSeePrim && r.prim_status === "OVERRIDE_ZERO" && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700"
                            data-testid={`dd-override-badge-${r.personnel_id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Prim sıfırlandı — Belediye/Trafik Override
                          </div>
                        )}
                        {/* Update 29.15 — Haftalık gün girişi (admin + data_entry edit; viewer readonly) */}
                        {isWeeklyMonth && (
                          <div className="mt-2 flex flex-wrap items-center gap-1" data-testid={`dd-weekly-${r.personnel_id}`}>
                            {["w1","w2","w3","w4"].map((k, i) => {
                              const max = (r.weekly_max || {})[k];
                              const val = getWeekVal(r, k);
                              return (
                                <div key={k} className="flex flex-col items-center">
                                  <label className="text-[9px] font-mono text-slate-500">H{i+1}·{max}g</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max={max}
                                    value={val ?? ""}
                                    placeholder="—"
                                    disabled={!canEdit || savingWeekly === r.personnel_id}
                                    onChange={(e) => setWeekVal(r, k, e.target.value)}
                                    className="w-11 h-9 min-h-[44px] sm:min-h-[36px] text-center text-xs border border-slate-300 rounded font-mono focus:ring-2 focus:ring-blue-200 disabled:bg-slate-50"
                                    data-testid={`dd-${k}-${r.personnel_id}`}
                                  />
                                </div>
                              );
                            })}
                            <div className="flex items-center gap-1 ml-1">
                              <span className="text-[10px] font-mono text-slate-600" data-testid={`dd-total-${r.personnel_id}`}>
                                Σ {["w1","w2","w3","w4"].reduce((s,k)=>s+Math.max(0,parseInt(getWeekVal(r,k)||0,10)),0)} / 30
                              </span>
                              {canEdit && hasPendingWeekly(r) && (
                                <Button size="sm" className="h-7 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => saveWeekly(r)}
                                        disabled={savingWeekly === r.personnel_id}
                                        data-testid={`dd-save-weekly-${r.personnel_id}`}>
                                  {savingWeekly === r.personnel_id ? "..." : "Kaydet"}
                                </Button>
                              )}
                            </div>
                            {!r.has_weekly_entry && (
                              <span className="text-[9px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5"
                                    data-testid={`dd-no-entry-${r.personnel_id}`}>
                                gün girilmedi · giriş-çıkışa göre
                              </span>
                            )}
                          </div>
                        )}
                        {/* Hesap satırı (admin + data_entry) */}
                        {canSeePrim && r.prim_calc && (
                          <div className="mt-1 text-[10px] font-mono text-slate-500" data-testid={`dd-calc-${r.personnel_id}`}>
                            {r.prim_calc.mode === "weekly" ? (
                              <>Taban {Math.round(r.prim_calc.base)} + Ek ({r.prim_calc.days_counted}g × {r.prim_calc.daily_extra_tl_effective?.toFixed(2)}) × {r.remaining_score}% = {r.bonus_tl.toFixed(2)}
                              {r.prim_calc.days_counted === 30 && ((r.weekly_days?.w1||0)+(r.weekly_days?.w2||0)+(r.weekly_days?.w3||0)+(r.weekly_days?.w4||0) > 30) && <span className="ml-1 text-amber-700">(üst sınır 30)</span>}</>
                            ) : (
                              <>Giriş-çıkış: {r.worked_days}/{r.days_in_month}g × {Math.round(r.prim_calc.base)} × {r.remaining_score}% = {r.bonus_tl.toFixed(2)}</>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[44px] h-7 px-2 rounded-full text-xs font-bold ${
                          r.penalty_total === 0 ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                          : r.penalty_total >= 50 ? "bg-red-100 text-red-800 border border-red-200"
                          : "bg-amber-100 text-amber-800 border border-amber-200"
                        }`}>
                          {r.penalty_total === 0 ? "0" : `−${r.penalty_total}`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="text-xs font-mono font-bold text-slate-800 shrink-0 w-14 text-right">{r.remaining_score} / 100</div>
                        </div>
                      </td>
                      {canSeePrim && (
                        <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{fmtTl(r.bonus_tl)}</td>
                      )}
                      <td className="px-4 py-3 text-center">
                        <button
                          className="text-blue-700 hover:underline text-xs font-medium"
                          onClick={() => setHistoryRow(r)}
                          data-testid={`dd-history-${r.personnel_id}`}
                        >
                          {r.violation_count > 0 ? `${r.violation_count} kayıt` : "Temiz"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {canEdit && (
                            <Button size="sm" variant="outline" onClick={() => openAdd(r.personnel_id)} data-testid={`dd-add-${r.personnel_id}`}>
                              <FilePlus className="w-3 h-3 mr-1" /> Ekle
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadKarne(r)}
                              disabled={karneLoadingId === r.personnel_id}
                              data-testid={`dd-karne-${r.personnel_id}`}
                            >
                              <FileDown className="w-3 h-3 mr-1" />
                              {karneLoadingId === r.personnel_id ? "..." : "Karne"}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add violation modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="dd-add-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Disiplin İhlali Kaydet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Şoför *</Label>
              <Select value={form.personnel_id} onValueChange={(v) => setForm({ ...form, personnel_id: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="dd-form-driver"><SelectValue placeholder="Şoför seçin" /></SelectTrigger>
                <SelectContent>
                  {data.rows.map((r) => (
                    <SelectItem key={r.personnel_id} value={r.personnel_id}>{r.personnel_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">İhlal Türü *</Label>
              <Select value={form.type_code} onValueChange={(v) => setForm({ ...form, type_code: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="dd-form-type"><SelectValue placeholder="İhlal seçin" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.label}{" "}
                      {t.prim_zero ? (
                        <span className="ml-2 text-red-700 font-bold text-xs">Primi sıfırlar</span>
                      ) : (
                        <span className="ml-2 text-red-600 font-bold text-xs">−{t.points} pt</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType?.prim_zero && (
                <div className="mt-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="dd-form-prim-zero-note">
                  Bu ihlal puan düşürmez — bu ay şoförün prim hakkı tamamen sıfırlanır.
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-600">Tarih *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 h-10 font-mono" data-testid="dd-form-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-600">Plaka</Label>
                <Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} placeholder="16 ADJ 678" className="mt-1 h-10 font-mono" data-testid="dd-form-plate" />
              </div>
            </div>
            {selectedType?.prim_zero && (
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-600">Ceza / Tutanak No</Label>
                  <Input
                    value={form.tutanak_no}
                    onChange={(e) => setForm({ ...form, tutanak_no: e.target.value })}
                    placeholder="MA-2026-00123"
                    className="mt-1 h-10 font-mono"
                    data-testid="dd-form-tutanak"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-600">Ceza Belgesi (foto / PDF, opsiyonel)</Label>
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    onChange={(e) => setAddFile(e.target.files?.[0] || null)}
                    className="mt-1 h-10 font-mono text-xs"
                    data-testid="dd-form-file"
                  />
                  {addFile && (
                    <div className="mt-1 text-[11px] text-slate-500 font-mono truncate">{addFile.name}</div>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Açıklama / Not</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder={selectedType?.prim_zero ? "Ceza konusu, meblağ, aracın durumu vb." : "Örn: 15A-4 aracını verdiğimde beğenmedi, seferden kaçtı."} data-testid="dd-form-desc" />
            </div>
          </div>
          <div className="flex justify-between mt-4">
            <Button variant="ghost" onClick={() => setAddOpen(false)}>İptal</Button>
            <Button
              onClick={submitViolation}
              className={selectedType?.prim_zero ? "bg-red-700 hover:bg-red-800" : "bg-[#1E3A8A] hover:bg-[#1e40af]"}
              data-testid="dd-form-submit"
            >
              {selectedType?.prim_zero ? "Ceza Kaydet · Primi Sıfırla" : "Kaydet ve Puanı Düş"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Violation history modal */}
      <Dialog open={!!historyRow} onOpenChange={(v) => !v && setHistoryRow(null)}>
        <DialogContent className="max-w-2xl" data-testid="dd-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <ShieldCheck className="w-5 h-5 text-blue-700" />
              {historyRow?.personnel_name} — İhlal Geçmişi ({month})
            </DialogTitle>
          </DialogHeader>
          {historyRow && (historyRow.violations || []).length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">Bu dönemde ihlal kaydı yok. Tebrikler!</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {(historyRow?.violations || []).map((v) => (
                <div key={v.id} className={`border rounded p-3 flex items-start gap-3 ${v.prim_zero || v.type_code === "TRAFIK_CEZASI" || v.type_code === "BURULAS_CEZASI" ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-500">{v.date}</span>
                      {v.plate && <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5">{v.plate}</span>}
                      <span className="text-sm font-semibold text-slate-900">{v.type_label}</span>
                      {canSeePrim && v.tutanak_no && (
                        <span className="font-mono text-[11px] text-slate-600" data-testid={`dd-vio-tutanak-${v.id}`}>
                          Tutanak: {v.tutanak_no}
                        </span>
                      )}
                      {(v.prim_zero || v.type_code === "TRAFIK_CEZASI" || v.type_code === "BURULAS_CEZASI") ? (
                        canSeePrim ? (
                          <span className="ml-auto text-red-700 font-bold text-xs" data-testid={`dd-vio-prim-zero-${v.id}`}>
                            Primi sıfırlar
                          </span>
                        ) : null
                      ) : (
                        <span className="ml-auto text-red-700 font-bold text-sm">−{v.points} pt</span>
                      )}
                    </div>
                    {v.description && <div className="text-xs text-slate-600 mt-1">{v.description}</div>}
                    {canSeePrim && v.document && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/driver-discipline/violations/${v.id}/document/${v.document.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                        data-testid={`dd-vio-doc-${v.id}`}
                      >
                        <FileDown className="w-3 h-3" />
                        Ceza belgesini aç
                      </a>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">Kayıt: {v.created_by_name || "—"}</div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => revokeViolation(v.id)}
                      className="p-2 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                      title="İptal et ve puanı iade et"
                      data-testid={`dd-revoke-${v.id}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  {!isAdmin && isDataEntry && v.created_by === user?.id && (
                    <button
                      onClick={() => revokeViolation(v.id)}
                      className="p-2 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                      title="Kendi kaydını iptal et ve puanı iade et"
                      data-testid={`dd-revoke-${v.id}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, color, mono }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
        <div className={`mt-1 font-heading font-bold text-2xl ${color} ${mono ? "font-mono" : ""}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
